import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, MapPin, Plus, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
    ApiCallError, getConsignees, saveConsignee, deactivateConsignee, getKnownConsigneeCodes,
    type ConsigneeDtoFull, type KnownConsigneeCodeDto,
} from '@/api/client';
import { codeIsKnown, codesMissingFromAddressBook } from '@/lib/consigneeCode';

/**
 * THE CONSIGNEE ADDRESS BOOK (#324, migration 171).
 *
 * Pete, 2026-08-27: *"Can we send a pin to the location and a .pdf?"*
 *
 * ---- ⭐⭐ THIS IS A PLACE, NOT A CUSTOMER ----------------------------------
 *
 * ⛔ IT IS NOT THE CLIENT LIST AND MUST NOT GROW INTO ONE. A client is whose
 * freight it is; a consignee is where it goes. Hellman is one client with several
 * drops, and the day somebody adds billing terms or a contact list to this screen
 * it stops being answerable to "where is the driver going" and starts being a
 * second, worse customer record.
 *
 * ---- ⛔ THE CODE IS THE JOIN, AND IT IS NOT A LABEL ------------------------
 *
 * ⚠️ `ConsigneeCode` matches `dock.AppointmentConsignee.ConsigneeCode` exactly —
 * no trimming, no case folding, no fuzzy match. A code typed with a trailing
 * space or a different case resolves to NOTHING, and the pin screen says
 * "not in the address book" while the row sits right here looking correct.
 *
 * ⭐ SO THE CODES ACTUALLY SEEN ON APPOINTMENTS ARE OFFERED (#328) — as a
 * datalist on the field, and as one-click chips for the ones not yet in the book.
 * ⛔ A WARNING UNDER THE FIELD WAS NOT ENOUGH: it asked a person to retype an
 * identifier accurately from memory, which is the thing people are worst at, and
 * it only worked on whoever happened to read it. ⚠️ The chips also make a typo
 * VISIBLE — a code that is not on the list is plainly a NEW one, rather than an
 * invisible near-miss of an existing one.
 *
 * ---- ⛔ DEACTIVATE, NEVER DELETE ------------------------------------------
 *
 * Nothing holds a foreign key here — the join is by code, from appointments that
 * may be years old. A delete would succeed silently and take the answer to
 * "where did that load go?" with it. ⚠️ An inactive row still RESOLVES: the pin
 * screen says "switched off", not "never heard of it", because those have
 * different fixes.
 */

interface Props {
    getToken: () => Promise<string>;
    /** ⭐ F130: opened from a rail card — load this consignee's form (or start one with its code). */
    focusCode?: string | null;
}

const BLANK = (): ConsigneeDtoFull => ({
    consigneeId: 0,
    consigneeCode: '',
    consigneeName: '',
    address: '',
    arrivalInstructions: '',
    mapLinkOverride: '',
    latitude: null,
    longitude: null,
    notes: '',
    isActive: true,
    createdUtc: null,
    createdBy: null,
    modifiedUtc: null,
    modifiedBy: null,
});

/**
 * ⛔ AN EMPTY BOX MEANS "NOT SET", NOT ZERO.
 *
 * `Number('')` is 0, and 0/0 is a real coordinate in the Atlantic off Ghana. A
 * blank latitude box that becomes 0 would place a consignee in the Gulf of Guinea
 * and the map link would open there confidently.
 */
const coordIn = (raw: string): number | null => {
    const t = raw.trim();
    if (t.length === 0) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
};

const coordOut = (v: number | null): string => (v === null || v === undefined ? '' : String(v));

export default function ConsigneeAdmin({ getToken, focusCode = null }: Props) {
    const [rows, setRows] = useState<ConsigneeDtoFull[]>([]);
    const [draft, setDraft] = useState<ConsigneeDtoFull>(BLANK());
    const [latText, setLatText] = useState('');
    const [lonText, setLonText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmOff, setConfirmOff] = useState<ConsigneeDtoFull | null>(null);
    const [known, setKnown] = useState<KnownConsigneeCodeDto[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            const [r, k] = await Promise.all([getConsignees(token), getKnownConsigneeCodes(token)]);
            setRows(r.consignees);
            setKnown(k.codes);
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Could not load the address book.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    const edit = (r: ConsigneeDtoFull) => {
        setDraft({ ...r });
        setLatText(coordOut(r.latitude));
        setLonText(coordOut(r.longitude));
        setError(null);
    };

    /* ⭐ F130: once the book is loaded, open the focused consignee. Not in the book → a new
       entry with its code filled in and nothing saved (the #328 rule). */
    useEffect(() => {
        if (!focusCode || loading) return;
        const hit = rows.find(r => (r.consigneeCode ?? '').toLowerCase() === focusCode.toLowerCase());
        if (hit) { edit(hit); return; }
        setDraft({ ...BLANK(), consigneeCode: focusCode });
        setLatText(''); setLonText('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusCode, loading, rows]);

    const reset = () => {
        setDraft(BLANK());
        setLatText('');
        setLonText('');
        setError(null);
    };

    /**
     * ⭐ #328 — one click fills the code AND the name last seen on a load.
     *
     * ⛔ IT DOES NOT SAVE. The name is a snapshot from whenever that load was
     * booked, and the address is still empty — this fills a form for a person to
     * check, not a row. Saving straight from a chip would put a possibly-stale
     * name into the address book on the authority of an old appointment.
     */
    const useKnownCode = (k: KnownConsigneeCodeDto) => {
        setDraft({ ...BLANK(), consigneeCode: k.consigneeCode, consigneeName: k.lastSeenName ?? '' });
        setLatText('');
        setLonText('');
        setError(null);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveConsignee(await getToken(), {
                consigneeId: draft.consigneeId || null,
                consigneeCode: draft.consigneeCode.trim(),
                consigneeName: draft.consigneeName.trim(),
                address: draft.address?.trim() || null,
                arrivalInstructions: draft.arrivalInstructions?.trim() || null,
                mapLinkOverride: draft.mapLinkOverride?.trim() || null,
                latitude: coordIn(latText),
                longitude: coordIn(lonText),
                notes: draft.notes?.trim() || null,
                isActive: draft.isActive,
            });
            reset();
            await load();
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Could not save that consignee.');
        } finally {
            setSaving(false);
        }
    };

    const doDeactivate = async () => {
        const target = confirmOff;
        setConfirmOff(null);
        if (!target) return;
        setError(null);
        try {
            await deactivateConsignee(await getToken(), target.consigneeId);
            await load();
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Could not switch that consignee off.');
        }
    };

    /**
     * ⚠️ BOTH OR NEITHER, MIRRORED FROM CK_Consignee_LatLong. Saying so here
     * rather than letting the server reject it means the person fixing it can see
     * which box is empty.
     */
    const halfPair = (latText.trim() === '') !== (lonText.trim() === '');
    const missing = codesMissingFromAddressBook(known);
    /**
     * ⛔ AN EXACT COMPARISON, MATCHING THE JOIN — see @/lib/consigneeCode for why
     * it must never be made case-insensitive, and the test that enforces it.
     */
    const typedCode = draft.consigneeCode.trim();
    const typedIsKnown = codeIsKnown(draft.consigneeCode, known);
    const canSave = draft.consigneeCode.trim().length > 0
        && draft.consigneeName.trim().length > 0
        && !halfPair
        && !saving;

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold">Consignees</h3>
                <p className="text-sm text-muted-foreground">
                    Where appointments are delivered. Used for the delivery pin texted to a driver, and
                    for scoping handouts to a destination.
                </p>
            </div>

            {error && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>{error}</span>
                </div>
            )}

            {/*
              * ⭐ #328 — the shortest path from "nothing is set up" to "every load
              * can be pinned". Each chip fills the form with a real code and the
              * name from its most recent load.
              *
              * ⚠️ IT DISAPPEARS WHEN THE LIST IS EMPTY rather than saying "all done".
              * A persistent green tick would be a claim about the future — the next
              * new consignee on the next booking brings this straight back.
              */}
            {missing.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                    <p className="text-xs font-medium text-amber-900">
                        {missing.length} consignee code{missing.length === 1 ? '' : 's'} on appointments
                        {missing.length === 1 ? ' is' : ' are'} not in the address book — appointments going
                        there cannot be sent a pin.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {missing.map(k => (
                            <button
                                key={k.consigneeCode}
                                type="button"
                                onClick={() => useKnownCode(k)}
                                className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                            >
                                <code>{k.consigneeCode}</code>
                                <span className="text-muted-foreground">
                                    {k.lastSeenName ?? '(no name)'} · {k.loadCount} load{k.loadCount === 1 ? '' : 's'}
                                </span>
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-amber-800">
                        Clicking one fills the form below — check the name, add an address, then save.
                    </p>
                </div>
            )}

            <div className="rounded-md border p-4 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="cons-code">Consignee code</Label>
                        <Input
                            id="cons-code"
                            list="known-consignee-codes"
                            value={draft.consigneeCode}
                            onChange={e => setDraft({ ...draft, consigneeCode: e.target.value })}
                            placeholder="HELL-AUR-01"
                            maxLength={100}
                        />
                        {/*
                          * ⭐ #328 — the codes actually on appointments. A datalist rather
                          * than a <select> because a genuinely new consignee must still be
                          * typeable: this is the destination address book, and a place can
                          * be added before its first load is booked.
                          */}
                        <datalist id="known-consignee-codes">
                            {known.map(k => (
                                <option key={k.consigneeCode} value={k.consigneeCode}>
                                    {k.lastSeenName ?? '(no name on the appointment)'} — {k.loadCount} load{k.loadCount === 1 ? '' : 's'}
                                </option>
                            ))}
                        </datalist>

                        {/*
                          * ⛔ THE FEEDBACK LINE IS THE POINT OF #328, NOT THE DATALIST.
                          * A datalist only helps someone who notices it. This says, about
                          * the code actually in the box, whether any appointment uses it —
                          * which is the thing that silently decides whether this row will
                          * ever do anything.
                          *
                          * ⚠️ "not on any appointment" IS NOT AN ERROR. A consignee can be
                          * added before its first load. It is a fact worth seeing, and the
                          * wording must not imply a mistake.
                          */}
                        {typedCode.length > 0 ? (
                            typedIsKnown ? (
                                <p className="text-xs text-green-700">
                                    Matches a code used on appointments — appointments going here will find it.
                                </p>
                            ) : (
                                <p className="text-xs text-amber-700">
                                    No appointment uses this code yet. That is fine for a new consignee —
                                    but if you meant an existing one, pick it from the list: the match is
                                    exact, so a difference in case or spacing counts as a different place.
                                </p>
                            )
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Must match the code on the appointment exactly — including case and
                                spacing. Start typing to pick from the codes already in use.
                            </p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cons-name">Name</Label>
                        <Input
                            id="cons-name"
                            value={draft.consigneeName}
                            onChange={e => setDraft({ ...draft, consigneeName: e.target.value })}
                            placeholder="Hellman Aurora"
                            maxLength={200}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="cons-addr">Address</Label>
                    <textarea
                        id="cons-addr"
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                        rows={3}
                        maxLength={400}
                        value={draft.address ?? ''}
                        onChange={e => setDraft({ ...draft, address: e.target.value })}
                        placeholder={'4400 Himalaya Rd\nAurora, CO 80019'}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="cons-arrival">Arrival / gate instructions</Label>
                    <textarea
                        id="cons-arrival"
                        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                        rows={2}
                        maxLength={1000}
                        value={draft.arrivalInstructions ?? ''}
                        onChange={e => setDraft({ ...draft, arrivalInstructions: e.target.value })}
                        placeholder="Gate 2, ring bell. Ask for shipping."
                    />
                    {/*
                      * ⚠️ THE SEGMENT WARNING BELONGS HERE, WHERE THE TEXT IS TYPED —
                      * not only on the send screen, where the person seeing it usually
                      * cannot change it.
                      */}
                    <p className="text-xs text-muted-foreground">
                        This is texted to the driver verbatim. Accented letters, curly quotes and
                        dashes pasted from Word cost more than twice as much to send — the pin
                        screen shows the segment count before anything goes out.
                    </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5 sm:col-span-1">
                        <Label htmlFor="cons-lat">Latitude</Label>
                        <Input
                            id="cons-lat" value={latText} inputMode="decimal"
                            onChange={e => setLatText(e.target.value)} placeholder="39.7834"
                        />
                    </div>
                    <div className="space-y-1.5 sm:col-span-1">
                        <Label htmlFor="cons-lon">Longitude</Label>
                        <Input
                            id="cons-lon" value={lonText} inputMode="decimal"
                            onChange={e => setLonText(e.target.value)} placeholder="-104.6892"
                        />
                    </div>
                    <div className="space-y-1.5 sm:col-span-1">
                        <Label htmlFor="cons-map">Map link (overrides everything)</Label>
                        <Input
                            id="cons-map" maxLength={500}
                            value={draft.mapLinkOverride ?? ''}
                            onChange={e => setDraft({ ...draft, mapLinkOverride: e.target.value })}
                            placeholder="https://maps.app.goo.gl/…"
                        />
                    </div>
                </div>

                {halfPair && (
                    <p className="text-xs text-destructive">
                        Give both a latitude and a longitude, or neither. Half a pair reads as
                        &ldquo;we know where this is&rdquo; and points at the wrong place.
                    </p>
                )}

                <p className="text-xs text-muted-foreground">
                    The pin uses the map link if there is one, then the coordinates, then the
                    address. A consignee with only an address still gets a usable pin.
                </p>

                <div className="space-y-1.5">
                    <Label htmlFor="cons-notes">Notes (internal — never texted)</Label>
                    <Input
                        id="cons-notes" maxLength={400}
                        value={draft.notes ?? ''}
                        onChange={e => setDraft({ ...draft, notes: e.target.value })}
                    />
                </div>

                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={draft.isActive}
                        onChange={e => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    Active
                </label>

                <div className="flex gap-2">
                    <Button onClick={() => void save()} disabled={!canSave}>
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                        {draft.consigneeId ? 'Save changes' : 'Add consignee'}
                    </Button>
                    {draft.consigneeId > 0 && (
                        <Button variant="outline" onClick={reset}>Cancel</Button>
                    )}
                </div>
            </div>

            <div className="rounded-md border">
                {loading ? (
                    <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">
                        No consignees yet. Add the destinations you deliver to, and a driver can be
                        texted a pin to any of them.
                    </p>
                ) : (
                    <ul className="divide-y">
                        {rows.map(r => (
                            <li key={r.consigneeId} className="flex items-start justify-between gap-3 p-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="font-medium truncate">{r.consigneeName}</span>
                                        <code className="text-xs text-muted-foreground">{r.consigneeCode}</code>
                                        {!r.isActive && (
                                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs">off</span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                        {r.address?.replace(/\s*\n\s*/g, ', ')
                                            || r.mapLinkOverride
                                            || (r.latitude !== null ? `${r.latitude}, ${r.longitude}` : null)
                                            /* ⛔ NOT "no address" — this row cannot produce a pin at
                                               all, and the send screen will say so. Say it here too,
                                               where it can be fixed. */
                                            || 'No address, coordinates or map link — cannot send a pin'}
                                    </p>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => edit(r)}>Edit</Button>
                                    {r.isActive && (
                                        <Button size="sm" variant="ghost" onClick={() => setConfirmOff(r)}>
                                            <PowerOff className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ⛔ NEVER window.confirm — see ConfirmDialog. */}
            <ConfirmDialog
                open={confirmOff !== null}
                title="Switch this consignee off?"
                message={
                    `${confirmOff?.consigneeName ?? ''} will stop being offered, and appointments going there `
                    + 'will say it is switched off rather than that it is unknown. Nothing is deleted — '
                    + 'historical appointments keep their address.'
                }
                confirmLabel="Switch off"
                onConfirm={() => void doDeactivate()}
                onCancel={() => setConfirmOff(null)}
            />
        </div>
    );
}
